import { initializeApp } from "firebase/app";
import { getMessaging, getToken } from "firebase/messaging";
import { ENV } from "./env";

// storage 
import { localStorage } from '@/infrastructure/storage/index';

// functions 
import { getTimeDifference } from '../functions/getTimeDifferent';
import { apiRequest } from '../services/main_api';


const firebaseConfig = {
  apiKey: ENV.FIREBASE_API_KEY,
  authDomain: ENV.FIREBASE_AUTH_DOMAIN,
  projectId: ENV.FIREBASE_PROJECT_ID,
  storageBucket: ENV.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: ENV.FIREBASE_MESSAGING_SENDER_ID,
  appId: ENV.FIREBASE_APP_ID,
  measurementId: ENV.FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
export const messaging = getMessaging(app);

// Use the VAPID Key you generated in Step 1
const VAPID_KEY = ENV.FIREBASE_VAPID_KEY;

export const requestForToken = async (
  nDays = 1,
  nDaysForceSync = 1,
  sendToBackend = true
): Promise<string | null> => {
  try {

    const syncTokenToBackend = async (token: string): Promise<boolean> => {
      if (!sendToBackend) return false;
      try {
        const response = await apiRequest({
          API: "/main-admin/admins/fcm-token",
          METHOD: "POST",
          DATA: { token },
        });
        if (response?.success) {
          console.log("✅ FCM token synced to backend");
          return true;
        }
        console.warn("⚠️ Backend rejected FCM token");
        return false;
      } catch (e) {
        console.warn("⚠️ Backend sync failed", e);
        return false;
      }
    };

    type TokenCache = {
      token: string;
      storedAt: number;
      sent_to_server: boolean;
      last_forced_sync_at?: number;
    };

    // ── read cache ────────────────────────────────────────────────────────
    let cached = localStorage.get<TokenCache>("fcm_token");

    // validate shape — if corrupted, treat as null
    if (
      cached &&
      !(
        typeof cached.token === "string" &&
        typeof cached.storedAt === "number" &&
        typeof cached.sent_to_server === "boolean"
      )
    ) {
      console.warn("⚠️ Corrupted FCM token cache, ignoring");
      cached = null;
    }

    // ── cache exists ──────────────────────────────────────────────────────
    if (cached) {
      const ageInDays = getTimeDifference(cached.storedAt, "days");

      // retry unsent token after nDays
      if (!cached.sent_to_server && ageInDays >= nDays) {
        const success = await syncTokenToBackend(cached.token);
        cached.storedAt = Date.now();
        if (success) cached.sent_to_server = true;
        localStorage.set("fcm_token", cached);
        return cached.token;
      }

      // force sync after nDaysForceSync
      if (cached.sent_to_server) {
        const lastForce = cached.last_forced_sync_at ?? cached.storedAt;
        const forceAge = getTimeDifference(lastForce, "days");
        if (forceAge >= nDaysForceSync) {
          await syncTokenToBackend(cached.token);
          cached.last_forced_sync_at = Date.now();
          localStorage.set("fcm_token", cached);
        }
      }

      // token still valid
      if (ageInDays < nDays) return cached.token;
    }

    // ── fetch fresh token ─────────────────────────────────────────────────
    const freshToken = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (!freshToken) return null;

    // token unchanged — just refresh timestamp
    if (cached && cached.token === freshToken) {
      cached.storedAt = Date.now();
      localStorage.set("fcm_token", cached);
      return freshToken;
    }

    // ── new / rotated token ───────────────────────────────────────────────
    const newPayload: TokenCache = {
      token: freshToken,
      storedAt: Date.now(),
      sent_to_server: false,
      last_forced_sync_at: Date.now(),
    };

    localStorage.set("fcm_token", newPayload);

    const success = await syncTokenToBackend(freshToken);
    if (success) {
      newPayload.sent_to_server = true;
      localStorage.set("fcm_token", newPayload);
    }

    return freshToken;

  } catch (err) {
    console.error("❌ Error retrieving FCM token", err);
    return null;
  }
};